import React, { useState, useEffect } from 'react';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Activity, Moon, Sun, ArrowLeft, Users, Heart,
  Mail, UserPlus, Trash2, CheckCircle, AlertCircle,
  Calendar, TrendingUp, MessageSquare, Bell, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FamilyCaregiverPortal() {
  const { user, token, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    relationship: 'spouse'
  });
  const [showInviteForm, setShowInviteForm] = useState(false);

  useEffect(() => {
    fetchFamilyMembers();
  }, []);

  const fetchFamilyMembers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/family/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFamilyMembers(data);
      }
    } catch (error) {
      console.error('Error fetching family members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/family/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(inviteForm)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.email_sent) {
          toast.success(`Invitation emailed to ${data.invited_email}`);
        } else {
          toast.success(`Invite created. Share this code: ${data.invite_code}`, {
            description: "We couldn't deliver the email right now — share the code directly.",
          });
        }
        setShowInviteForm(false);
        setInviteForm({ email: '', name: '', relationship: 'spouse' });
        fetchFamilyMembers();
      } else {
        toast.error('Failed to send invite');
      }
    } catch (error) {
      toast.error('Error sending invite');
    }
  };

  const removeMember = async (memberId) => {
    try {
      const response = await fetch(`${API_URL}/api/family/members/${memberId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Family member access removed');
        fetchFamilyMembers();
      } else {
        toast.error('Failed to remove member');
      }
    } catch (error) {
      toast.error('Error removing member');
    }
  };

  const relationships = [
    { value: 'spouse', label: 'Spouse/Partner' },
    { value: 'child', label: 'Child' },
    { value: 'parent', label: 'Parent' },
    { value: 'sibling', label: 'Sibling' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-cn-bone-100 via-white to-cn-bone-200 dark:from-cn-ink-950 dark:via-cn-ink-900 dark:to-cn-ink-950" data-testid="family-portal">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200/50 dark:border-cn-ink-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <CaraLoomLogo size={30} />
            </Link>

            <div className="flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-cn-ink-800 transition-colors"
              >
                {theme === 'light' ? <Moon className="w-5 h-5 text-slate-600" /> : <Sun className="w-5 h-5 text-yellow-400" />}
              </button>
              <span className="text-sm text-slate-600 dark:text-slate-300">
                {user?.first_name} {user?.last_name}
              </span>
              <Button variant="ghost" onClick={logout} className="text-slate-600 dark:text-slate-300">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <Link to="/patient/dashboard" className="inline-flex items-center gap-2 text-cn-primary dark:text-cn-primary hover:underline mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        <div className="space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-display">
                Family Caregiver Portal
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-2">
                Invite family members to stay connected with your care journey
              </p>
            </div>
            <Button
              onClick={() => setShowInviteForm(!showInviteForm)}
              className="bg-gradient-to-r from-cn-primary to-cn-sage-500 hover:from-cn-primary hover:to-cn-sage-600 text-white rounded-full px-6"
              data-testid="invite-family-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Family Member
            </Button>
          </div>

          {/* Invite Form */}
          {showInviteForm && (
            <Card className="border-0 shadow-lg bg-white dark:bg-cn-ink-800" data-testid="invite-form">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-cn-primary" />
                  Invite Family Member
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleInvite} className="space-y-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Name
                      </label>
                      <Input
                        type="text"
                        value={inviteForm.name}
                        onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                        placeholder="Family member's name"
                        required
                        data-testid="invite-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Email
                      </label>
                      <Input
                        type="email"
                        value={inviteForm.email}
                        onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                        placeholder="email@example.com"
                        required
                        data-testid="invite-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                        Relationship
                      </label>
                      <select
                        value={inviteForm.relationship}
                        onChange={(e) => setInviteForm({ ...inviteForm, relationship: e.target.value })}
                        className="w-full h-10 px-3 rounded-md border border-slate-200 dark:border-cn-ink-700 bg-white dark:bg-cn-ink-800 text-slate-900 dark:text-white"
                        data-testid="invite-relationship-select"
                      >
                        {relationships.map((rel) => (
                          <option key={rel.value} value={rel.value}>{rel.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button type="submit" className="bg-gradient-to-r from-cn-primary to-cn-sage-500 text-white rounded-full" data-testid="send-invite-btn">
                      Send Invite
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowInviteForm(false)} className="rounded-full">
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Info Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-gradient-to-br from-cn-primary to-cn-sage-500 dark:from-cn-sage-900/20 dark:to-cn-sage-900/20 border-0">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-cn-sage-100 dark:bg-cn-sage-900/40 rounded-xl flex items-center justify-center mb-4">
                  <Eye className="w-6 h-6 text-cn-primary dark:text-cn-primary" />
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">View Care Updates</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Family members can view daily check-ins, appointments, and care progress
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-cn-sage-50 to-green-50 dark:from-cn-sage-900/20 dark:to-green-900/20 border-0">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-cn-sage-100 dark:bg-cn-sage-900/40 rounded-xl flex items-center justify-center mb-4">
                  <Bell className="w-6 h-6 text-cn-primary dark:text-cn-sage-300" />
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Get Notifications</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Receive alerts about important health updates and care changes
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-cn-terracotta-50 to-cn-terracotta-50 dark:from-cn-terracotta-700/20 dark:to-cn-terracotta-700/20 border-0">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-cn-terracotta-100 dark:bg-cn-terracotta-700/40 rounded-xl flex items-center justify-center mb-4">
                  <MessageSquare className="w-6 h-6 text-cn-accent dark:text-cn-terracotta-300" />
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Coordinate Care</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Communicate with clinicians and help manage appointments
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Family Members List */}
          <Card className="border-0 shadow-lg bg-white dark:bg-cn-ink-800" data-testid="family-members-list">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-cn-primary" />
                Care Circle Members
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-4 border-cn-primary border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : familyMembers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-500 dark:text-slate-400">No family members invited yet</p>
                  <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                    Invite family members to help coordinate your care
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {familyMembers.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-cn-ink-700/50"
                      data-testid={`family-member-${member.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-cn-primary to-cn-sage-500 dark:from-cn-sage-900/40 dark:to-cn-sage-900/40 rounded-full flex items-center justify-center">
                          <Heart className="w-6 h-6 text-cn-primary dark:text-cn-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{member.name}</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{member.email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs px-2 py-0.5 bg-cn-sage-100 dark:bg-cn-sage-900/40 text-cn-primary-hover dark:text-cn-sage-300 rounded-full capitalize">
                              {member.relationship}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              member.status === 'active' 
                                ? 'bg-cn-sage-100 dark:bg-cn-sage-900/40 text-cn-sage-700 dark:text-cn-sage-300' 
                                : 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300'
                            }`}>
                              {member.status === 'active' ? (
                                <><CheckCircle className="w-3 h-3 inline mr-1" />Active</>
                              ) : (
                                <><AlertCircle className="w-3 h-3 inline mr-1" />Pending</>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMember(member.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        data-testid={`remove-member-${member.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
